import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { Search, Plus, PencilLine, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DialogShell, Field } from "@/components/common/FormShell";
import type { ApiEnvelope, PageQuery, PageResponse } from "@/lib/api/types";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

/**
 * One admin screen for a piece of reference data (specializations, product
 * categories, medicines, lab tests): searchable table + create/edit dialog
 * + delete confirmation, in the same style as the hand-written pages.
 * Each screen is just a config — columns, form fields and API calls.
 */

type Row = { id: string };

const EMPTY_PAGE = {
  data: { code: "00", data: { items: [], page: 0, size: 0, totalItems: 0, totalPages: 0 }, errors: [] },
};

export interface CatalogColumn<T> {
  header: string;
  render: (item: T) => React.ReactNode;
  className?: string;
}

export interface CatalogField {
  key: string;
  label: string;
  type?: "text" | "textarea" | "number" | "checkbox";
  required?: boolean;
  placeholder?: string;
  /** Two short fields share a row. */
  half?: boolean;
  /** Initial state of a checkbox when creating (default: unchecked). */
  defaultChecked?: boolean;
}

type ListSource<T> =
  | {
      /** Small lists the server returns whole; searched in the browser. */
      kind: "list";
      fetch: () => Promise<AxiosResponse<ApiEnvelope<T[]>>>;
      searchText: (item: T) => string;
    }
  | {
      /** Paged + searched on the server. */
      kind: "paged";
      fetch: (q: PageQuery) => Promise<AxiosResponse<ApiEnvelope<PageResponse<T>>>>;
    };

export interface CatalogPageProps<T extends Row> {
  queryKey: string;
  title: string;
  subtitle: (count: number) => string;
  icon: LucideIcon;
  noun: string;
  source: ListSource<T>;
  columns: CatalogColumn<T>[];
  fields: CatalogField[];
  itemLabel: (item: T) => string;
  create: (body: Record<string, unknown>) => Promise<unknown>;
  update: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  remove?: (id: string) => Promise<unknown>;
  searchPlaceholder: string;
  /** Extra buttons rendered before edit/delete on every row. */
  rowActions?: (item: T) => React.ReactNode;
}

export function CatalogPage<T extends Row>(props: CatalogPageProps<T>) {
  const { queryKey, title, subtitle, icon: Icon, noun, source, columns, fields, itemLabel } = props;
  const qc = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);

  // Two data sources behind one table.
  const paged = usePagedQuery<T>({
    key: [queryKey],
    // (hooks cannot be conditional: in list mode it runs against an empty page)
    fetcher: source.kind === "paged" ? source.fetch : () => Promise.resolve(EMPTY_PAGE as unknown as AxiosResponse<ApiEnvelope<PageResponse<T>>>),
  });
  const [localQ, setLocalQ] = useState("");
  const whole = useQuery({
    queryKey: [queryKey, "all"],
    queryFn: () => (source.kind === "list" ? source.fetch().then((r) => r.data.data ?? []) : Promise.resolve([] as T[])),
    enabled: source.kind === "list",
  });
  const localItems = useMemo(() => {
    if (source.kind !== "list") return [];
    const needle = localQ.trim().toLowerCase();
    const all = whole.data ?? [];
    return needle ? all.filter((i) => source.searchText(i).toLowerCase().includes(needle)) : all;
  }, [source, whole.data, localQ]);

  const isPaged = source.kind === "paged";
  const items = isPaged ? paged.items : localItems;
  const total = isPaged ? paged.totalItems : (whole.data?.length ?? 0);
  const fetching = isPaged ? paged.isFetching : whole.isFetching;
  const q = isPaged ? paged.q : localQ;
  const setQ = isPaged ? paged.setQ : setLocalQ;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: [queryKey] });
    setEditorOpen(false);
    setEditing(null);
    setDeleting(null);
  };
  const remove = useMutation({
    mutationFn: (id: string) => props.remove!(id),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle(total)}
        action={
          <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
            <Plus className="size-4" /> Add {noun}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border p-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder={props.searchPlaceholder} />
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={Icon}
              title={fetching ? "Loading…" : `No ${noun}s`}
              description={q ? "Nothing matches this search." : `Add the first ${noun}.`}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => <TableHead key={c.header} className={c.className}>{c.header}</TableHead>)}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    {columns.map((c) => <TableCell key={c.header} className={c.className ?? "text-sm"}>{c.render(item)}</TableCell>)}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {props.rowActions?.(item)}
                        <Button size="sm" variant="outline" onClick={() => { setEditing(item); setEditorOpen(true); }}>
                          <PencilLine className="size-3.5" />
                        </Button>
                        {props.remove && (
                          <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" onClick={() => setDeleting(item)}>
                            <Trash2 className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {isPaged && (
            <Pagination page={paged.page} size={PAGE_SIZE} totalItems={paged.totalItems} totalPages={paged.totalPages} onChange={paged.setPage} />
          )}
        </CardContent>
      </Card>

      <CatalogEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        noun={noun}
        fields={fields}
        item={editing as Record<string, unknown> | null}
        onSubmit={(body) => (editing ? props.update(editing.id, body) : props.create(body))}
        onSaved={refresh}
      />

      {props.remove && (
        <ConfirmDialog
          open={!!deleting}
          onOpenChange={(v) => !v && setDeleting(null)}
          onConfirm={() => remove.mutate(deleting!.id)}
          icon={Trash2}
          tone="destructive"
          title={`Delete this ${noun}?`}
          description={`"${deleting ? itemLabel(deleting) : ""}" is removed permanently.`}
          confirmLabel={`Delete ${noun}`}
          loading={remove.isPending}
        />
      )}
    </>
  );
}

function CatalogEditor({
  open, onOpenChange, noun, fields, item, onSubmit, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  noun: string;
  fields: CatalogField[];
  item: Record<string, unknown> | null;
  onSubmit: (body: Record<string, unknown>) => Promise<unknown>;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string | boolean> = {};
    for (const f of fields) {
      const v = item?.[f.key];
      next[f.key] = f.type === "checkbox"
        ? (v === undefined || v === null ? Boolean(f.defaultChecked) : Boolean(v))
        : v == null ? "" : String(v);
    }
    setValues(next);
    setError(null);
  }, [open, item, fields]);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      for (const f of fields) {
        const v = values[f.key];
        if (f.type === "checkbox") body[f.key] = Boolean(v);
        else if (f.type === "number") body[f.key] = v === "" ? null : Number(v);
        else body[f.key] = typeof v === "string" && v.trim() !== "" ? v.trim() : null;
      }
      const res = (await onSubmit(body)) as AxiosResponse<ApiEnvelope<unknown>>;
      // Business failures arrive as HTTP 200 too: a successful create/update
      // returns the saved record, a refusal returns no data plus a code.
      if (!res?.data?.data) {
        const first = res?.data?.errors?.[0];
        throw new Error(first?.message ?? `The server refused this (${res?.data?.code ?? "unknown"}).`);
      }
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  const valid = fields.every((f) => !f.required || (typeof values[f.key] === "string" && (values[f.key] as string).trim() !== ""));

  const input = (f: CatalogField) => {
    const v = values[f.key];
    if (f.type === "checkbox") {
      return (
        <label key={f.key} className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            checked={Boolean(v)}
            onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.checked }))}
            className="size-4 accent-[var(--color-primary)]"
          />
          {f.label}
        </label>
      );
    }
    const label = f.required ? `${f.label} *` : f.label;
    return (
      <Field key={f.key} label={label}>
        {f.type === "textarea" ? (
          <textarea
            value={String(v ?? "")}
            onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
            rows={3}
            placeholder={f.placeholder}
            className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
        ) : (
          <Input
            type={f.type === "number" ? "number" : "text"}
            value={String(v ?? "")}
            placeholder={f.placeholder}
            onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
          />
        )}
      </Field>
    );
  };

  // Lay half-width fields out two per row.
  const rows: CatalogField[][] = [];
  for (const f of fields) {
    const last = rows[rows.length - 1];
    if (f.half && last && last.length === 1 && last[0].half) last.push(f);
    else rows.push([f]);
  }

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      wide={fields.length > 6}
      icon={item ? <PencilLine className="size-7" /> : <Plus className="size-7" />}
      title={item ? `Edit ${noun}` : `Add ${noun}`}
      description={item ? "Changes show in the app immediately." : `Creates a new ${noun} for the app.`}
    >
      <div className="mt-5 space-y-3 text-left">
        {rows.map((row, i) =>
          row.length === 2 ? (
            <div key={i} className="grid grid-cols-2 gap-3">{row.map(input)}</div>
          ) : (
            input(row[0])
          ),
        )}
        {error && <p className="rounded-lg bg-destructive/10 p-2 text-sm text-destructive">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : item ? "Save changes" : `Create ${noun}`}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
